<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGameLink } from '@/services/GameLink'
import * as Alerts from '@/services/Alerts'
import * as Auth from '@/services/Auth'
import * as Notice from '@/services/Notice'

/**
 * Button that takes the player into the game using the signed link (see services/GameLink.js).
 * Use it wherever the player has to get on track: the link refreshes itself.
 *
 * The game server is closed to accounts that have not been activated yet: there the button is
 * replaced by the text that says how to activate the account, with the button that sends the
 * activation link again to the address the account already has.
 *
 * The component can render two different roots (the button, or that text), so the attributes
 * coming from the caller — `class` above all, used to size the call to action — are bound by hand.
 *
 * Showing this button is also the sign that the player is about to race: it is what asks for the
 * one-off notice for new players, so that the notice appears on the first page they reach with it.
 */
defineOptions({ inheritAttrs: false })

defineProps({
  label: { type: String, default: '' },
  // The link to open, when the caller has its own (one button per server, see PlayView); empty
  // means the shared one of GameLink.
  href: { type: String, default: '' },
  color: { type: String, default: 'primary' },
  variant: { type: String, default: 'flat' },
  size: { type: String, default: 'default' },
  prependIcon: { type: String, default: 'mdi-play' },
  block: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
})

const { t } = useI18n()

const { url, loading } = useGameLink()

const sending = ref(false)

// The check is silent, happens once per session and does nothing for an account that cannot play
// yet: the notice itself is a single dialog mounted in App.vue (see services/Notice.js).
onMounted(() => Notice.check())

async function sendActivationEmail() {
  sending.value = true

  try {
    await Auth.sendActivationEmail()
    Alerts.success(t('alerts.verifyEmailSent'))
  } catch (e) {
    Alerts.error(t('alerts.verifyEmailError'))
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <!-- Account waiting to be activated: no way into the game, only the way to activate it -->
  <div
    v-if="!Auth.isVerified()"
    v-bind="$attrs"
    class="d-flex flex-column align-center justify-center text-center"
  >
    <v-icon size="36" color="warning" class="mb-2">mdi-email-alert</v-icon>
    <p class="text-body-2 mb-3">{{ $t('play.activateAccount') }}</p>
    <v-btn
      color="warning"
      variant="tonal"
      prepend-icon="mdi-email-sync"
      :loading="sending"
      :disabled="sending"
      @click="sendActivationEmail"
    >
      {{ $t('actions.verifyEmail') }}
    </v-btn>
  </div>

  <v-btn
    v-else
    v-bind="$attrs"
    :href="(href || url) ?? undefined"
    :color="color"
    :variant="variant"
    :size="size"
    :prepend-icon="prependIcon"
    :block="block"
    :loading="loading"
    :disabled="disabled || !(href || url)"
  >
    <slot>{{ label }}</slot>
  </v-btn>
</template>
